import { Flex, Box, BoxProps, Tooltip, Text, Heading } from "@chakra-ui/react";
import React from "react";

export interface ValueIntervalChartPoint {
  startValue: number;
  endValue: number;
  type: string;
  color?: string;
  order?: number;
  label?: string;
}

export interface Props extends BoxProps {
  startValue: number;
  endValue: number;
  intervals: ValueIntervalChartPoint[];
}

export const ValueIntervalChart: React.FC<Props> = ({
  startValue,
  endValue,
  intervals,
  ...rest
}) => {
  if (intervals.length === 0) {
    return null;
  }
  const fillIntervals = (
    data: ValueIntervalChartPoint[],
    startValue: number,
    endValue: number
  ): ValueIntervalChartPoint[] => {
    if (data.length === 0) return [];
    const intervals: ValueIntervalChartPoint[] = [];

    data.forEach((it, index) => {
      const previousValue =
        index === 0 ? startValue : data[index - 1]?.endValue;

      if (previousValue !== it.startValue) {
        intervals.push({
          startValue: previousValue,
          endValue: it.startValue,
          type: "none",
          order: 0,
        });
      }

      intervals.push(it);
    });

    if (data[data.length - 1].endValue !== endValue) {
      intervals.push({
        startValue: data[data.length - 1].endValue,
        endValue: endValue,
        type: "none",
        order: 0,
      });
    }

    return intervals.sort((a, b) => a.startValue - b.startValue);
  };
  const totalValue = endValue - startValue;

  return (
    <Box background="secondary.300" height="15px" position="relative" {...rest}>
      {intervals
        .map((it) => it.type) // group by type
        .filter((v, i, a) => a.indexOf(v) === i) // filter unique
        .map((type) => (
          <Flex key={type} position="absolute" width="100%" height="100%">
            {fillIntervals(
              intervals.filter((it) => it.type === type),
              startValue,
              endValue
            ).map((interval, index) => {
              const valueDiff = interval.endValue - interval.startValue;
              const width = valueDiff / totalValue;
              const label = (
                <Text as="span">
                  {interval.color !== undefined ? (
                    <>
                      {interval.label ?? type}
                      <br />
                    </>
                  ) : (
                    ""
                  )}
                  {interval.startValue !== interval.endValue
                    ? `${interval.startValue} - ${interval.endValue}`
                    : interval.startValue}
                </Text>
              );
              return (
                <Tooltip key={index} label={label} placement="left-end">
                  <Heading
                    variant="h7Strong"
                    fontSize="md"
                    minWidth="fit-content"
                    textAlign="center"
                    width={`${width * 100}%`}
                    background={interval.color}
                    zIndex={
                      interval.color !== undefined
                        ? (interval.order ?? 0) + 1
                        : undefined
                    }>
                    {interval.label}
                  </Heading>
                </Tooltip>
              );
            })}
          </Flex>
        ))}
    </Box>
  );
};
